import { useState, useEffect } from "react";
import {
  listTournamentsForScope,
  type TournamentListItem,
} from "../api/tournament/tournamentService";
import {
  listAcademiesForScope,
  type AcademyListItem,
  listCentersV2ForScopeIds,
} from "../api/academy/academyService";
import {
  listVenuesForScope,
  type VenueListItem,
} from "../api/venue/venueService";
import {
  listShopSellersForScope,
  type ShopSellerItem,
} from "../api/shop/sellerService";
import { listClubsForScope, type ClubListItem } from "../api/club/clubService";

export interface ScopeIdOption {
  value: string;
  label: string;
}

interface UseScopeIdsResult {
  scopeIdOptions: ScopeIdOption[];
  loading: boolean;
  error: string | null;
}

export function useScopeIds(
  scopeType?: string | null,
  academyIdForCoachingCenter?: string | null,
  search?: string
): UseScopeIdsResult {
  const [scopeIdOptions, setScopeIdOptions] = useState<ScopeIdOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchScopeIds() {
      if (!scopeType) {
        if (isMounted) {
          setScopeIdOptions([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        let options: ScopeIdOption[] = [];

        if (scopeType === "tournament") {
          const tournaments: TournamentListItem[] =
            await listTournamentsForScope();
          options = (tournaments || []).map((t) => ({
            value: t._id,
            label: t.name,
          }));
        } else if (scopeType === "academy") {
          const academies: AcademyListItem[] = await listAcademiesForScope();
          options = (academies || []).map((a) => ({
            value: a._id,
            label: a.basicDetails?.academyName ?? "",
          }));
        } else if (scopeType === "venue") {
          const venues: VenueListItem[] = await listVenuesForScope();
          options = (venues || []).map((v) => ({
            value: v._id,
            label: v.basicDetails?.name ?? "",
          }));
        } else if (scopeType === "club" || scopeType === "clubs") {
          const clubs: ClubListItem[] = await listClubsForScope();
          options = (clubs || []).map((c) => ({
            value: c._id,
            label: c.name,
          }));
        } else if (scopeType === "shop_seller") {
          const sellers: ShopSellerItem[] = await listShopSellersForScope();
          options = (sellers || []).map((s) => ({
            value: s._id,
            label: s.name,
          }));
        } else if (scopeType === "coaching_center" || scopeType === "coach") {
          // Coach/coaching-center scopes use direct centers listing (list-v2).
          // Academy selection is no longer required.
          const centers: any[] = await listCentersV2ForScopeIds(search);
          options = (centers || []).map((c) => ({
            value: c?._id ?? "",
            label:
              c?.basicDetails?.locationDisplay ??
              c?.basicDetails?.location ??
              "",
          }));
        } else {
          options = [];
        }

        if (isMounted) {
          setScopeIdOptions(options);
        }
      } catch (err) {
        console.error("Failed to fetch scope IDs:", err);
        if (isMounted) {
          setError("Failed to load scope IDs");
          setScopeIdOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchScopeIds();

    return () => {
      isMounted = false;
    };
  }, [scopeType, academyIdForCoachingCenter, search]);

  return { scopeIdOptions, loading, error };
}

